// reportUtils/dataCalculations.js

export const getSectionStats = (filteredChartData, isCountMode, overviewState) => {
    const sectionStats = {};
    
    filteredChartData.forEach((gameData, gameIndex) => {
      const dataArrays = isCountMode 
        ? gameData.historicalCountData || []
        : gameData.historicalQueueData || [];
      
      const transactionArrays = gameData.historicalTransactionData || [];
      
      dataArrays.forEach(sectionData => {
        const sectionId = sectionData.section;
        
        if (!sectionStats[sectionId]) {
          sectionStats[sectionId] = {
            sectionId,
            totalDataPoints: 0,
            maxValue: 0,
            minValue: Infinity,
            sumValue: 0,
            gameCount: 0,
            peakGameIndex: -1,
            gameBreakdown: [],
            transactionSum: 0,
            transactionCount: 0
          };
        }
        
        const stats = sectionStats[sectionId];
        
        let validPointsInThisGame = 0;
        let maxInThisGame = 0;
        let sumInThisGame = 0;
        
        sectionData.data.forEach((point, idx) => {
          if (typeof point.y === 'number' && point.y >= 0) {
            validPointsInThisGame++;
            stats.totalDataPoints++;
            sumInThisGame += point.y;
            
            if (point.y > maxInThisGame) {
              maxInThisGame = point.y;
            }
            
            if (point.y > stats.maxValue) {
              stats.maxValue = point.y;
              stats.peakGameIndex = gameIndex;
            }
            stats.minValue = Math.min(stats.minValue, point.y);
            stats.sumValue += point.y;
          }
        });
        
        // Calculate this game's average
        const thisGameAverage = validPointsInThisGame > 0 
          ? Math.round(sumInThisGame / validPointsInThisGame) 
          : 0;
        
        // Get transaction data for this section in this game
        let thisGameTransactionAvg = undefined;
        let thisGameTransactionPeak = undefined;
        if (!isCountMode) {
          const sectionTransaction = transactionArrays.find(tx => tx.section === sectionId);
          if (sectionTransaction && sectionTransaction.data && sectionTransaction.data.length > 0) {
            const txSum = sectionTransaction.data.reduce((acc, point) => acc + (point.y || 0), 0);
            thisGameTransactionAvg = Math.round(txSum / sectionTransaction.data.length);
            
            // Calculate peak transaction for this game
            const txValues = sectionTransaction.data.map(point => point.y || 0).filter(v => v > 0);
            thisGameTransactionPeak = txValues.length > 0 ? Math.max(...txValues) : 0;
            
            stats.transactionSum += txSum;
            stats.transactionCount += sectionTransaction.data.length;
          }
        }
        
        // Store game breakdown
        stats.gameBreakdown.push({
          hasData: validPointsInThisGame > 0,
          peak: maxInThisGame,
          avg: thisGameAverage,
          avgTransaction: thisGameTransactionAvg,
          peakTransaction: thisGameTransactionPeak,
          dataPoints: validPointsInThisGame
        });
        
        stats.gameCount++;
      });
    });
    
    // Add placeholder entries for sections that don't have data in all games
    Object.values(sectionStats).forEach(stat => {
      while (stat.gameBreakdown.length < filteredChartData.length) {
        stat.gameBreakdown.push({
          hasData: false,
          peak: 0,
          avg: 0,
          avgTransaction: undefined,
          peakTransaction: undefined,
          dataPoints: 0
        });
      }
    });
    
    const statsArray = Object.values(sectionStats).map(stat => {
      const avgValue = stat.totalDataPoints > 0 
        ? Math.round(stat.sumValue / stat.totalDataPoints) 
        : 0;
      
      const avgTransaction = stat.transactionCount > 0
        ? Math.round(stat.transactionSum / stat.transactionCount)
        : undefined;
      
      return {
        ...stat,
        avgValue,
        avgTransaction,
        minValue: stat.minValue === Infinity ? 0 : stat.minValue
      };
    });
    
    statsArray.sort((a, b) => b.maxValue - a.maxValue);
    
    return statsArray;
};

export const formatTimeOffset = (minutes) => {
    if (minutes === 0) return 'Game Start';//'First Pitch';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    let timeString;
    if (mins === 0) {
      timeString = `${hours}h`;
    } else {
      timeString = `${hours}h ${mins}m`;
    }
    if (minutes > 0) {
      return `+${timeString}`;
    } else {
      return `-${timeString}`;
    }
  };
  
export const captureChartCanvas = async (chartContainer) => {
    if (!chartContainer) return null;
    try {
      const canvas = chartContainer.querySelector('canvas');
      if (canvas) {
        return {
          dataUrl: canvas.toDataURL('image/png', 1.0),
          width: canvas.width,
          height: canvas.height,
          aspectRatio: canvas.width / canvas.height
        };
      } else {
        const rect = chartContainer.getBoundingClientRect();
        const html2canvasResult = await window.html2canvas(chartContainer, {
          scale: 4,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
          allowTaint: true,
          width: rect.width,
          height: rect.height,
        });
        return {
          dataUrl: html2canvasResult.toDataURL('image/png', 1.0),
          width: html2canvasResult.width,
          height: html2canvasResult.height,
          aspectRatio: html2canvasResult.width / html2canvasResult.height
        };
      }
    } catch (error) {
      console.error('Error capturing chart:', error);
      return null;
    }
};

export const calculateHourlyBreakdowns = (filteredChartData, isCountMode, overviewState, timeRange) => {
    const sectionHourlyStats = {};
    
    filteredChartData.forEach((gameData, gameIdx) => {
      const gameDate = overviewState.selectedDates[gameIdx];
      const gameStartTime = overviewState.gameStartTimes[gameIdx];
      
      const firstPitchTime = new Date(`${gameDate}T${gameStartTime}:00`);
      
      const dataArrays = isCountMode 
        ? gameData.historicalCountData || []
        : gameData.historicalQueueData || [];
      
      const transactionArrays = gameData.historicalTransactionData || [];
      
      dataArrays.forEach(sectionData => {
        const sectionId = sectionData.section;
        
        if (!sectionHourlyStats[sectionId]) {
          sectionHourlyStats[sectionId] = {};
        }
        
        if (!sectionHourlyStats[sectionId][gameIdx]) {
          sectionHourlyStats[sectionId][gameIdx] = {};
        }
        
        const timeBlocks = generateTimeBlocks(timeRange);
        
        timeBlocks.forEach(block => {
          const blockKey = `${block.startOffset}_${block.endOffset}`;
          
          // Filter data points for this time block
          const blockDataPoints = sectionData.data.filter(point => {
            const pointTime = new Date(point.x);
            const minutesFromFirstPitch = (pointTime - firstPitchTime) / (1000 * 60);
            return minutesFromFirstPitch >= block.startOffset && minutesFromFirstPitch < block.endOffset;
          });
          
          if (blockDataPoints.length > 0) {
            // Group queue/count points by minute (matching the other view's logic)
            const dataByMinute = {};
            
            blockDataPoints.forEach(point => {
              const pointTime = new Date(point.x);
              const minuteKey = pointTime.toISOString().slice(0, 16);
              
              if (!dataByMinute[minuteKey]) {
                dataByMinute[minuteKey] = [];
              }
              dataByMinute[minuteKey].push(point.y);
            });
            
            // Take max of each minute, then calculate stats from those
            const minuteMaxes = Object.values(dataByMinute).map(valuesInMinute => 
              Math.max(...valuesInMinute.filter(v => typeof v === 'number' && v >= 0))
            );
            
            const peak = Math.max(...minuteMaxes);
            const avg = Math.round(minuteMaxes.reduce((sum, v) => sum + v, 0) / minuteMaxes.length);
            
            // Get transaction data for this block
            let avgTransaction = undefined;
            let peakTransaction = undefined;
            if (!isCountMode) {
              const sectionTransaction = transactionArrays.find(tx => tx.section === sectionId);
              if (sectionTransaction && sectionTransaction.data) {
                // Group transaction points by minute (matching the other view's logic)
                const transactionByMinute = {};
                
                sectionTransaction.data.forEach(point => {
                  const pointTime = new Date(point.x);
                  const minutesFromFirstPitch = (pointTime - firstPitchTime) / (1000 * 60);
                  
                  // Check if point is in this time block
                  if (minutesFromFirstPitch >= block.startOffset && minutesFromFirstPitch < block.endOffset) {
                    // Group by minute key (truncate to minute)
                    const minuteKey = pointTime.toISOString().slice(0, 16);
                    
                    if (!transactionByMinute[minuteKey]) {
                      transactionByMinute[minuteKey] = [];
                    }
                    transactionByMinute[minuteKey].push(point.y || 0);
                  }
                });
                
                // Take max of each minute, then average those
                const minuteMaxes = Object.values(transactionByMinute).map(timesInMinute => 
                  Math.max(...timesInMinute)
                );
                
                if (minuteMaxes.length > 0) {
                  const sum = minuteMaxes.reduce((acc, val) => acc + val, 0);
                  avgTransaction = Math.round(sum / minuteMaxes.length);
                  peakTransaction = Math.max(...minuteMaxes);
                }
              }
            }
            
            sectionHourlyStats[sectionId][gameIdx][blockKey] = {
              peak,
              avg,
              avgTransaction,
              peakTransaction,
              dataPoints: blockDataPoints.length,
              label: block.label
            };
          } else {
            sectionHourlyStats[sectionId][gameIdx][blockKey] = {
              peak: 0,
              avg: 0,
              avgTransaction: undefined,
              peakTransaction: undefined,
              dataPoints: 0,
              label: block.label
            };
          }
        });
      });
    });
    
    return sectionHourlyStats;
};
  
const generateTimeBlocks = (timeRange) => {
    const blocks = [];
    const { startOffset, endOffset } = timeRange;
    
    let currentStart = startOffset;
    
    while (currentStart < endOffset) {
      let blockEnd;
      
      if (currentStart < 0) {
        blockEnd = Math.floor(currentStart / 60) * 60 + 60;
      } else if (currentStart === 0) {
        blockEnd = 60;
      } else {
        blockEnd = Math.floor(currentStart / 60) * 60 + 60;
      }
      
      blockEnd = Math.min(blockEnd, endOffset);
      
      if (blockEnd <= currentStart) {
        console.warn(`Invalid block: start=${currentStart}, end=${blockEnd}`);
        break;
      }
      
      blocks.push({
        startOffset: currentStart,
        endOffset: blockEnd,
        label: formatTimeBlockLabel(currentStart, blockEnd)
      });
      
      currentStart = blockEnd;
    }
    
    return blocks;
};
  
const formatTimeBlockLabel = (startMinutes, endMinutes) => {
    const formatTime = (mins) => {
      if (mins === 0) return 'Game Start';//'First Pitch';
      const absMinutes = Math.abs(mins);
      const hours = Math.floor(absMinutes / 60);
      const minutes = absMinutes % 60;
      
      let str = '';
      if (hours > 0) str += `${hours}h`;
      if (minutes > 0) str += `${hours > 0 ? ' ' : ''}${minutes}m`;
      
      if (mins < 0) {
        return `-${str.trim()}`;
      } else {
        return `+${str.trim()}`;
      }
    };
    
    const start = formatTime(startMinutes);
    const end = formatTime(endMinutes);
    
    return `${start} to ${end}`;
};